// DÖF formları (DofCreate / DofEdit / DofList) için ortak yardımcılar

// Backend tarafından yönetilen / formdan gönderilmemesi gereken alanlar
const SERVER_MANAGED_FIELDS = [
  "dofNo", // DÖF No sistem tarafından otomatik verilir
  "_id",
  "__v",
  "_access",
  "createdAt",
  "updatedAt",
  "isActive",
  "actions",
  "createdBy",
  "status",
  "closedAt",
  "notifications",
  "userId",
  "_termin",
  "lastNote",
  "submittedAt",
  "approvedAt",
  "lastReminderAt",
  "kaynakDofNo",
  "yeniDofId",
  "yeniDofNo",
];

export const BOOLEAN_FIELDS = [
  "tekrarUygunsuzluk",
  "icDenetim",
  "disDenetim",
  "iyilestirme",
  "icProsesSikayet",
  "musteriSikayet",
  "yeniDofAcilmali",
  "prosedur",
  "talimat",
  "isAkisi",
  "form",
];

// select value'su için boolean -> "true"/"false"/""
export const boolVal = (value) =>
  value === true || value === "true"
    ? "true"
    : value === false || value === "false"
    ? "false"
    : "";

// Boş stringleri ve backend alanlarını temizleyip API'ye gidecek payload'ı hazırlar.
// Not: Eskiden "Seçim Yapınız" seçili kalan evet/hayır alanları "" gidiyor ve backend validasyonu reddediyordu.
export const cleanDofPayload = (formData) => {
  const payload = {};

  Object.entries(formData || {}).forEach(([key, value]) => {
    if (SERVER_MANAGED_FIELDS.includes(key)) return;
    if (value === undefined || value === null) return;
    // Evet/Hayır alanlarında "Seçim Yapınız" -> gönderme (metin alanlarında "" gönderilir ki temizlenebilsin)
    if (value === "" && BOOLEAN_FIELDS.includes(key)) return;
    payload[key] = value;
  });

  payload.assignedUsers = (formData?.assignedUsers || []).map((item) => ({
    userId: item.userId ? String(item.userId) : undefined,
    fullName: item.fullName || "",
    email: String(item.email || "").trim().toLowerCase() || undefined,
    departman: item.departman || "",
    gorev: item.gorev || "",
    assignmentType: item.assignmentType || "sorumlu",
    ...(item.assignedAt ? { assignedAt: Number(item.assignedAt) } : {}),
  }));

  return payload;
};

export const ASSIGNMENT_LABELS = {
  sorumlu: "Sorumlu",
  bilgi: "Bilgi",
  onayci: "Onaycı",
  takipci: "Takipçi",
};

export const REQUIRED_WORKFLOW_ROLES = ["sorumlu", "onayci", "takipci"];

export const getMissingWorkflowRoleLabels = (assignedUsers = []) =>
  REQUIRED_WORKFLOW_ROLES.filter(
    (role) => !assignedUsers.some((item) => item.assignmentType === role)
  ).map((role) => ASSIGNMENT_LABELS[role]);

export const FALIYET_LABELS = {
  duzeltici: "Düzeltici",
  onleyici: "Önleyici",
};

export const formatDofDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("tr-TR");
};

// <input type="date"> sadece YYYY-MM-DD kabul eder
export const toDateInputValue = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const getTespitTuruMetni = (dof) => {
  const turler = [];
  if (dof?.tekrarUygunsuzluk) turler.push("Tekrarlayan Uygunsuzluk");
  if (dof?.icDenetim) turler.push("İç Denetim");
  if (dof?.disDenetim) turler.push("Dış Denetim");
  if (dof?.iyilestirme) turler.push("İyileştirme");
  if (dof?.icProsesSikayet) turler.push("İç Proses Şikayeti");
  if (dof?.musteriSikayet) turler.push("Müşteri Şikayeti");
  if (dof?.digerAciklama) turler.push(`Diğer: ${dof.digerAciklama}`);
  return turler.join(", ");
};

// İş akışı durumları
export const DOF_STATUSES = [
  { key: "acik", label: "Açık", long: "Faaliyet Girişi", badge: "bg-warning text-dark", color: "#ff6600", bg: "#ffe6cc" },
  { key: "onay_bekliyor", label: "Onay Bekliyor", long: "Onay Bekliyor", badge: "bg-primary", color: "#0d6efd", bg: "#dbe8ff" },
  { key: "takipte", label: "Takipte", long: "Etkinlik Takibi", badge: "bg-info text-dark", color: "#0a8ea8", bg: "#d6f4fa" },
  { key: "kapali", label: "Kapalı", long: "Kapalı", badge: "bg-success", color: "#009933", bg: "#ccffcc" },
];

export const getDofStatus = (dof) => {
  if (DOF_STATUSES.some((s) => s.key === dof?.status)) return dof.status;
  return String(dof?.dofKapatmaTarihiOnay || "").trim() ? "kapali" : "acik";
};

export const getStatusMeta = (status) =>
  DOF_STATUSES.find((s) => s.key === status) || DOF_STATUSES[0];

export const isDofClosed = (dof) => getDofStatus(dof) === "kapali";

export const todayInputValue = () => toDateInputValue(new Date());

// Ad soyaddan baş harfler (avatar için)
export const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toLocaleUpperCase("tr-TR"))
    .join("") || "?";

export const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
};

// "3 gün önce" gibi göreli zaman
export const timeAgo = (value) => {
  if (!value) return "";
  const diff = Date.now() - new Date(value).getTime();
  if (Number.isNaN(diff)) return "";
  const min = Math.round(diff / 60000);
  if (min < 1) return "az önce";
  if (min < 60) return `${min} dk önce`;
  const hour = Math.round(min / 60);
  if (hour < 24) return `${hour} saat önce`;
  const day = Math.round(hour / 24);
  if (day < 30) return `${day} gün önce`;
  return formatDofDate(value);
};

// Termine kalan gün (negatif = gecikme)
export const daysUntil = (value) => {
  const d = toDateInputValue(value);
  if (!d) return null;
  const today = toDateInputValue(new Date());
  return Math.round((new Date(d) - new Date(today)) / 86400000);
};
